import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/base/stores/authStore';

interface VaultStatus {
  isSetup: boolean;
  isUnlocked: boolean;
  ttlRemaining?: number;
  unlockedAt?: string;
  expiresAt?: string;
}

interface VaultContextValue {
  // Status
  status: VaultStatus | null;
  isLoading: boolean;
  isUnlocked: boolean;
  isSetup: boolean;
  ttlRemaining: number | null;

  // Actions
  requestUnlock: () => void;
  lockVault: () => Promise<void>;
  lockAndSignOut: () => Promise<void>;

  // Modal state (for post-login flow and emergency unlock)
  showUnlockModal: boolean;
  dismissUnlockModal: () => void;

  // Refetch
  refetchStatus: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const VaultContext = createContext<VaultContextValue | null>(null);

export function VaultProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());
  const hasShownPostLoginRef = useRef(false);
  const extendIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check if user is authenticated
  const token = localStorage.getItem('auth_token');
  const isAuthenticated = !!token;

  // Query vault status
  const { data: status, isLoading, refetch } = useQuery<VaultStatus>({
    queryKey: ['vault', 'status'],
    queryFn: async () => {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        throw new Error('No auth token');
      }

      const response = await fetch('/api/v1/cvs/vault/status', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch vault status');
      }

      return response.json();
    },
    refetchInterval: 30000, // 30 seconds
    staleTime: 10000, // 10 seconds
    enabled: isAuthenticated,
  });

  // Track user activity
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, []);

  // Auto-extend vault session when unlocked
  useEffect(() => {
    const EXTEND_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

    if (status?.isUnlocked) {
      // Clear any existing interval
      if (extendIntervalRef.current) {
        clearInterval(extendIntervalRef.current);
      }

      // Set up 5-minute interval to extend vault
      extendIntervalRef.current = setInterval(async () => {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;

        // Only extend if user has been active in the last 5 minutes
        if (timeSinceLastActivity < EXTEND_INTERVAL_MS) {
          try {
            const authToken = localStorage.getItem('auth_token');
            if (!authToken) return;

            await fetch('/api/v1/cvs/vault/extend', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`,
              },
            });

            // Refetch status after extending
            refetch();
          } catch (error) {
            console.error('Failed to extend vault session:', error);
          }
        }
      }, EXTEND_INTERVAL_MS);
    } else {
      // Clear interval when vault is locked
      if (extendIntervalRef.current) {
        clearInterval(extendIntervalRef.current);
        extendIntervalRef.current = null;
      }
    }

    return () => {
      if (extendIntervalRef.current) {
        clearInterval(extendIntervalRef.current);
        extendIntervalRef.current = null;
      }
    };
  }, [status?.isUnlocked, refetch]);

  // Post-login prompt logic
  useEffect(() => {
    if (!status || hasShownPostLoginRef.current || isLoading) {
      return;
    }

    // Show unlock modal if vault is not set up OR if it's set up but locked
    if (!status.isSetup || (status.isSetup && !status.isUnlocked)) {
      setShowUnlockModal(true);
      hasShownPostLoginRef.current = true;
    }
  }, [status, isLoading]);

  // Actions
  const requestUnlock = useCallback(() => {
    setShowUnlockModal(true);
  }, []);

  const dismissUnlockModal = useCallback(() => {
    setShowUnlockModal(false);
  }, []);

  const lockVault = useCallback(async () => {
    try {
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        throw new Error('No auth token');
      }

      const response = await fetch('/api/v1/cvs/vault/lock', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to lock vault');
      }

      // Invalidate vault status query to trigger refetch
      await queryClient.invalidateQueries({ queryKey: ['vault', 'status'] });
    } catch (error) {
      console.error('Failed to lock vault:', error);
      throw error;
    }
  }, [queryClient]);

  const lockAndSignOut = useCallback(async () => {
    try {
      // Lock vault first
      await lockVault();
    } catch (error) {
      console.error('Failed to lock vault during sign out:', error);
      // Continue with sign out even if lock fails
    }

    // Sign out
    useAuthStore.getState().signOut();
  }, [lockVault]);

  const refetchStatus = useCallback(() => {
    refetch();
  }, [refetch]);

  const contextValue: VaultContextValue = {
    // Status
    status: status || null,
    isLoading,
    isUnlocked: status?.isUnlocked ?? false,
    isSetup: status?.isSetup ?? false,
    ttlRemaining: status?.ttlRemaining ?? null,

    // Actions
    requestUnlock,
    lockVault,
    lockAndSignOut,

    // Modal state
    showUnlockModal,
    dismissUnlockModal,

    // Refetch
    refetchStatus,
  };

  return (
    <VaultContext.Provider value={contextValue}>
      {children}
    </VaultContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useVault(): VaultContextValue {
  const context = useContext(VaultContext);

  if (!context) {
    throw new Error('useVault must be used within a VaultProvider');
  }

  return context;
}
